// app/components/Hero.tsx
"use client";

import { useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import Image from "next/image";

const GREEN = "rgb(34 150 83)";

const wavePaths = [
  "M0,60 C200,10 400,90 600,50 C800,10 1000,90 1200,60 L1200,100 L0,100 Z",
  "M0,40 C200,90 400,10 600,70 C800,90 1000,10 1200,40 L1200,100 L0,100 Z",
  "M0,60 C200,10 400,90 600,50 C800,10 1000,90 1200,60 L1200,100 L0,100 Z",
];

export default function Hero() {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { once: true });

  /* Cursor gradient orb (desktop only visually) */
  const mx = useMotionValue(50);
  const my = useMotionValue(50);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mx.set((e.clientX / window.innerWidth) * 100);
      my.set((e.clientY / window.innerHeight) * 100);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  /* Image carousel (DESKTOP ONLY VISUALLY) */
  const [imgIndex, setImgIndex] = useState(0);
  const images = ["/8.jpeg", "/5.jpeg", "/3.jpeg"];

  useEffect(() => {
    const timer = setInterval(
      () => setImgIndex((i) => (i + 1) % images.length),
      4000
    );
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      ref={rootRef}
      id="home"
      className="relative  py-32 max-lg:py-24 max-sm:py-20 overflow-hidden bg-gradient-to-b from-gray-900 via-emerald-950 to-gray-900"
    >
      {/* LEFT PANEL (FULL WIDTH ON MOBILE, SAME ON DESKTOP) */}
      <div className="absolute inset-y-0 left-0 right-[52%] max-lg:relative max-lg:right-0 max-lg:w-full bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900">
        <motion.div
          className="absolute inset-0 opacity-25 max-lg:opacity-20"
          style={{
            background: useTransform([mx, my], ([x, y]) => {
              return `radial-gradient(circle 700px at ${x}% ${y}%, ${GREEN}, transparent 70%)`;
            }),
          }}
        />
      </div>

      {/* RIGHT IMAGE SIDE (DESKTOP ONLY — COMPLETELY HIDDEN ON MOBILE) */}
      <div className="absolute inset-y-0 md:left-[48%] right-0 max-lg:hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={imgIndex}
            className="absolute inset-0 h-full w-full"
            initial={{ opacity: 0.7, scale: 1.01 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0.5, scale: 1.01 }}
            transition={{ duration: 1.2 }}
          >
            <Image
              src={images[imgIndex]}
              alt="Pinak Dental Clinic Interior"
              fill
              className="object-cover"
              priority
            />
          </motion.div>
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-gray-900/30" />
      </div>

      {/* SVG DIVIDERS (DESKTOP ONLY) */}
      <svg
        className="absolute inset-y-0 left-[43%] z-10 h-full w-24 max-lg:hidden"
        viewBox="0 0 100 1000"
        preserveAspectRatio="none"
      >
        <motion.path
          d="M50,0 Q80,250 50,500 T50,1000"
          fill="none"
          stroke="url(#waveGradient)"
          strokeWidth="3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
        <defs>
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(34,150,83,1)" />
            <stop offset="100%" stopColor="rgba(34,150,83,0.5)" />
          </linearGradient>
        </defs>
      </svg>

      <svg
        className="absolute inset-y-0 left-[43%] z-10 h-full w-24 max-lg:hidden"
        viewBox="0 0 100 1000"
        preserveAspectRatio="none"
      >
        <motion.path
          d="M80,0 Q80,250 50,500 T80,1000"
          fill="none"
          stroke="url(#waveGradient2)"
          strokeWidth="3"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
        <defs>
          <linearGradient id="waveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(34,150,83,1)" />
            <stop offset="100%" stopColor="rgba(34,150,83,0.5)" />
          </linearGradient>
        </defs>
      </svg>

      {/* CONTENT */}
      <div className="relative z-20 flex h-full items-center md:p-0 pt-20">
        <div
          className="
      mx-auto
      grid w-full max-w-9xl grid-cols-12 gap-8
      px-6 lg:px-12
      max-lg:grid-cols-1
      max-lg:justify-items-center
    "
        >
          <div
            className="
        col-span-12
        flex flex-col justify-center
        lg:col-span-6
        max-lg:items-center
        max-lg:text-center
        max-lg:mx-auto
      "
          >
            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1, duration: 0.7 }}
              className="text-5xl font-black leading-[1.1] text-white lg:text-5xl max-sm:text-3xl"
            >
              Premier Dental Clinic in {" "}<br></br>
              <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-200 bg-clip-text text-transparent">
               RT Nagar
              </span>
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2, duration: 0.7 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-gray-300 max-sm:text-base"
            >

Searching for a dental clinic near you? Choose <strong>Pinak Dental Clinic</strong> , RT Nagar. With 10+ specialists, advanced technology, aligners, veneers, smile designing, and comprehensive dental care, we help you achieve a healthy, confident smile.            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="mt-8 flex flex-col sm:flex-row gap-4 max-lg:items-center"
            >
              <motion.a
                href="#contact"
                className="rounded-full px-10 py-4 font-semibold text-white shadow-lg shadow-emerald-500/30"
                style={{ backgroundColor: GREEN }}
              >
                Book Appointment →
              </motion.a>

              <motion.button
                onClick={() =>
                  document
                    .getElementById("treatments")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="rounded-full border-2 border-white/30 bg-white/5 px-10 py-4 font-semibold text-white backdrop-blur"
              >
                View Treatments
              </motion.button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.7 }}
              className="hidden mt-12 md:grid grid-cols-4 gap-4 max-lg:grid-cols-2 max-lg:justify-items-center"
            >
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center backdrop-blur">
                <p className="text-4xl font-bold text-white">1000+</p>
                <p className="mt-1 text-sm text-gray-400">Happy Patients</p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center backdrop-blur">
                <p className="text-4xl font-bold text-white">4+</p>
                <p className="mt-1 text-sm text-gray-400">Years Experience</p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-center backdrop-blur">
                <p className="text-4xl font-bold text-white">10+</p>
                <p className="mt-1 text-sm text-gray-400">Specialists</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom wave divider */}
      <div className="absolute left-0 right-0 bottom-0 z-30 w-full">
        <svg
          className="w-full h-16 md:h-24"
          viewBox="0 0 1200 100"
          preserveAspectRatio="none"
        >
          <motion.path
            d={wavePaths[0]}
            fill="white"
            animate={{ d: wavePaths }}
            transition={{ repeat: Infinity, duration: 10 }}
          />
        </svg>
      </div>
    </section>
  );
}
